import { useEffect, useState } from "react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { breadcrumbSchema, collectionSchema } from "@/lib/seo";

const filters = ["All", "Laser Engraved", "UV Printed"];

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("All");
  const [status, setStatus] = useState("loading");

  const load = () => {
    setStatus("loading");
    api.get("/products")
      .then((r) => { setProducts(r.data); setStatus("ready"); })
      .catch(() => setStatus("error"));
  };

  useEffect(() => { load(); }, []);

  const visible = filter === "All" ? products : products.filter((p) => p.method === filter);

  return (
    <main data-testid="shop-page" className="pt-40 pb-32 min-h-screen">
      <Seo
        title="Shop Personalised Engraved Gifts & Drinkware — Leduc, Alberta | Ember & Steel Studio"
        description="Shop laser-engraved tumblers, YETI and Stanley drinkware, walnut boards, slate coasters, pet tags, metal business cards and UV-printed keepsakes. Personalised in Leduc, Alberta and shipped across Canada and to the USA. Prices in CAD."
        path="/shop"
        jsonLd={[
          breadcrumbSchema([["Home", "/"], ["Shop", "/shop"]]),
          collectionSchema({ name: "Shop — personalised engraved gifts & drinkware", description: "Laser-engraved and UV-printed pieces made to order in Leduc, Alberta.", path: "/shop", items: products.map((p) => [p.name, `/shop/${p.slug}`]) }),
        ]}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <span className="font-mono-tech text-xs uppercase tracking-[0.35em] text-[#C87455]">The Collection</span>
          <h1 className="font-serif-display font-light text-4xl sm:text-5xl lg:text-6xl text-white mt-4 tracking-tight">
            Pieces built to outlast you
          </h1>
          <p className="text-zinc-400 mt-6 max-w-xl">
            Every item is personalised in-house. Add your engraving at the product page —
            we handle the rest.
          </p>
        </Reveal>

        <div className="flex gap-3 mt-12 mb-14 flex-wrap">
          {filters.map((f) => (
            <button
              key={f}
              data-testid={`shop-filter-${f.toLowerCase().replace(" ", "-")}`}
              onClick={() => setFilter(f)}
              className={`font-mono-tech text-xs uppercase tracking-[0.2em] px-5 py-2.5 border transition-colors duration-300 ${
                filter === f
                  ? "border-[#C87455] text-[#C87455] bg-[#C87455]/5"
                  : "border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-zinc-300"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {status === "loading" &&
            [...Array(6)].map((_, i) => (
              <div key={i} className="bg-[#0C0C0C] border border-zinc-800/60 animate-pulse" data-testid="shop-skeleton">
                <div className="aspect-[4/5] bg-zinc-900/60" />
                <div className="px-5 py-5 space-y-3 border-t border-zinc-800/60">
                  <div className="h-4 w-2/3 bg-zinc-900/80" />
                  <div className="h-3 w-1/3 bg-zinc-900/60" />
                </div>
              </div>
            ))}
          {status === "ready" && visible.map((p, i) => (
            <ProductCard key={p.slug} product={p} index={i} />
          ))}
        </div>
        {status === "error" && (
          <div className="text-center py-24" data-testid="shop-error">
            <p className="font-serif-display text-2xl text-zinc-400">The collection couldn't load.</p>
            <button
              data-testid="shop-retry-btn"
              onClick={load}
              className="mt-6 bg-[#C87455] text-black hover:bg-[#A65B3E] transition-colors px-8 py-3 font-mono-tech uppercase tracking-[0.2em] text-sm"
            >
              Try Again
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
